import { z } from 'zod';

import { CustomAttributeType } from '@/modules/common/types/custom-attribute';

const I18N_PREFIX = 'authenticated.settings:pages.membersCustomAttributes.errors';

/**
 * Error message keys, exported so the table cell can treat them differently:
 * - duplicate → highlight the field red and show the message
 * - required → neither (a not-yet-filled new row shouldn't look like an error; the
 *   disabled Save button + its tooltip communicate the requirement instead)
 */
export const DUPLICATE_NAME_ERROR_KEY = `${I18N_PREFIX}.duplicateName`;
export const NAME_REQUIRED_ERROR_KEY = `${I18N_PREFIX}.nameRequired`;

/**
 * A single option in an attribute row. Existing options carry a positive backend id;
 * draft options created in the UI carry a negative temporary id.
 */
export const customAttributeOptionSchema = z.object({
  id: z.number(),
  value: z.string().trim().min(1),
});

/**
 * Validation schema for a single custom attribute row.
 *
 * Only single/multi select types are supported on this screen, so every attribute
 * must define at least one option (otherwise the attribute is unusable).
 */
export const customAttributeSchema = z.object({
  // Negative for unsaved draft rows, positive for persisted rows.
  attributeId: z.number(),
  name: z
    .string()
    .trim()
    .min(1, `${I18N_PREFIX}.nameRequired`)
    .max(100, `${I18N_PREFIX}.nameTooLong`),
  type: z.nativeEnum(CustomAttributeType),
  options: z.array(customAttributeOptionSchema).min(1, `${I18N_PREFIX}.optionsRequired`),
});

/**
 * Form-level schema with case-insensitive duplicate-name validation across rows.
 * Every row that shares a name with another row is flagged (not just the later
 * duplicates) so the error shows on all of them.
 */
export const customAttributesFormSchema = z.object({
  attributes: z.array(customAttributeSchema).superRefine((attributes, ctx) => {
    // Group row indices by normalized name, then flag every row in any group of size > 1.
    const indicesByName = new Map<string, number[]>();

    attributes.forEach((attr, index) => {
      const normalizedName = attr.name.toLowerCase().trim();
      if (!normalizedName) return;

      const indices = indicesByName.get(normalizedName) ?? [];
      indices.push(index);
      indicesByName.set(normalizedName, indices);
    });

    indicesByName.forEach(indices => {
      if (indices.length < 2) return;
      indices.forEach(index => {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${I18N_PREFIX}.duplicateName`,
          path: [index, 'name'],
        });
      });
    });
  }),
});

export type CustomAttributeOptionFormData = z.infer<typeof customAttributeOptionSchema>;
export type CustomAttributeRowFormData = z.infer<typeof customAttributeSchema>;
export type CustomAttributesFormData = z.infer<typeof customAttributesFormSchema>;
